import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Check, Database, Flag, Lock, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import {
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { ReconStepSearch } from "..";
import {
  monthLabel,
  useBilledActuals,
  usePullBilledActuals,
  useReconPeriod,
  useReviewBilledAccount,
  type BilledAccount,
  type ReviewStatus,
} from "../api";
import {
  filterAccounts,
  sortAccounts,
  sourcesMismatch,
  totalAccounts,
  type BilledFilters,
  type ChangeKind,
} from "../billedActuals";
import { fmtCur2, relativeAgo } from "../../renewals/format";
import { BilledAccountDrawer } from "./BilledAccountDrawer";

const CHANGE_LABELS: { value: ChangeKind; label: string }[] = [
  { value: "increased", label: "Went up" },
  { value: "decreased", label: "Went down" },
  { value: "new", label: "New this month" },
  { value: "stopped", label: "Stopped billing" },
  { value: "flat", label: "Flat" },
];

const REVIEW_LABELS: { value: ReviewStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "flagged", label: "Flagged" },
];

/** R-01 worklist: the month's billed actuals per account (waterfall + raw-QB
 *  cross-check), row expansion into the invoice lines, per-account review
 *  (approve / flag + notes), and the pull-from-Snowflake action. */
export function BilledActualsPanel({ period }: { period: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReconStepSearch;
  const setSearch = (next: Partial<ReconStepSearch>) =>
    navigate({
      to: "/reconciliation/$period/$stepKey",
      params: { period, stepKey: "R-01" },
      search: (prev: ReconStepSearch) => ({ ...prev, ...next }),
      replace: true,
    });

  const { data, isLoading, error } = useBilledActuals(period);
  const pull = usePullBilledActuals(period);
  const review = useReviewBilledAccount(period);
  const [sort, setSort] = useState<DataTableSort | null>({
    key: "billed_current",
    dir: "desc",
  });
  // Whether the drawer was opened via a Flag button (preselects the verdict).
  const [flagIntent, setFlagIntent] = useState(false);

  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const filters: BilledFilters = useMemo(
    () => ({
      q: search.q ?? "",
      review: (search.review ?? "").split(",").filter(Boolean) as ReviewStatus[],
      change: (search.change ?? "").split(",").filter(Boolean) as ChangeKind[],
      mismatch: !!search.mismatch,
      mrr: !!search.mrr,
    }),
    [search],
  );
  const rows = useMemo(
    () => sortAccounts(filterAccounts(accounts, filters), sort),
    [accounts, filters, sort],
  );
  const all = useMemo(() => totalAccounts(accounts), [accounts]);
  const shown = useMemo(() => totalAccounts(rows), [rows]);
  const drawerAccount = useMemo(
    () => accounts.find((a) => a.qb_id === search.account) ?? null,
    [accounts, search.account],
  );

  // A completed R-01 is locked: the snapshot + reviews are frozen (the
  // backend enforces it; the UI hides the actions).
  const { data: periodDetail } = useReconPeriod(period);
  const locked =
    periodDetail?.steps.find((s) => s.step_key === "R-01")?.locked ?? false;
  const canAct = (data?.can_act ?? false) && !locked;
  const pulled = !!data?.pulled_at;
  const invoicingConfirmed = data?.invoicing_confirmed ?? false;
  const curLabel = monthLabel(period);
  const priorLabel = monthLabel(data?.prior_period ?? "");

  function doPull() {
    pull.mutate(undefined, {
      onSuccess: (p) =>
        toast.success(
          `Pulled billed actuals — ${p.summary?.account_count ?? 0} accounts for ${curLabel}`,
        ),
      onError: (e) =>
        toast.error("Pull failed", { description: (e as Error).message }),
    });
  }

  function quickApprove(a: BilledAccount) {
    review.mutate(
      { qbId: a.qb_id, review: { status: "approved" } },
      {
        onError: (e) =>
          toast.error("Couldn't approve", { description: (e as Error).message }),
      },
    );
  }

  function openDrawer(a: BilledAccount, flag: boolean) {
    setFlagIntent(flag);
    setSearch({ account: a.qb_id });
  }

  const columns: DataTableColumn<BilledAccount>[] = [
    {
      key: "qb_id",
      header: "QB ID",
      sortable: true,
      className: "w-20",
      render: (a) => (
        <span className="font-mono text-xs text-muted-foreground">{a.qb_id}</span>
      ),
    },
    {
      key: "cinch_account_name",
      header: "Account (Cinch)",
      sortable: true,
      className: "max-w-[240px]",
      render: (a) =>
        a.cinch_account_name ? (
          <span className="block truncate text-sm font-semibold" title={a.cinch_account_name}>
            {a.cinch_account_name}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground" title="No Cinch account carries this QuickBooks id">
            no Cinch account
          </span>
        ),
    },
    {
      key: "qb_customer_name",
      header: "QB customer",
      sortable: true,
      className: "max-w-[240px]",
      render: (a) => (
        <span className="block truncate text-sm" title={a.qb_customer_name ?? undefined}>
          {a.qb_customer_name ?? "—"}
        </span>
      ),
    },
    {
      key: "billed_prior",
      header: `Billed ${priorLabel}`,
      sortable: true,
      numeric: true,
      render: (a) => fmtCur2(a.billed_prior),
    },
    {
      key: "delta",
      header: "Δ",
      sortable: true,
      align: "center",
      render: (a) => <DeltaCell value={a.billed_current - a.billed_prior} />,
    },
    {
      key: "billed_current",
      header: `Billed ${curLabel}`,
      sortable: true,
      numeric: true,
      render: (a) => <span className="font-semibold">{fmtCur2(a.billed_current)}</span>,
    },
    {
      key: "mrr_current",
      header: "MRR",
      sortable: true,
      numeric: true,
      render: (a) => fmtCur2(a.mrr_current),
    },
    {
      key: "qb_check",
      header: "QB Δ",
      sortable: true,
      numeric: true,
      render: (a) =>
        sourcesMismatch(a) ? (
          <span
            className="font-semibold text-warning"
            title={`QuickBooks nets ${fmtCur2(a.qb_invoice_total ?? 0)} vs waterfall ${fmtCur2(a.billed_current)}`}
          >
            {fmtCur2((a.qb_invoice_total ?? 0) - a.billed_current)}
          </span>
        ) : (
          <span className="text-muted-foreground" title="Waterfall and QuickBooks agree">
            ✓
          </span>
        ),
    },
    {
      key: "review",
      header: "Review",
      sortable: true,
      align: "right",
      className: "w-44",
      render: (a) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge value={a.review_status} />
          {canAct && (
            <>
              <button
                type="button"
                aria-label={`Approve ${a.cinch_account_name ?? a.qb_id}`}
                title="Approve — numbers look right"
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded hover:bg-success-light hover:text-success",
                  a.review_status === "approved"
                    ? "text-success"
                    : "text-muted-foreground",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  quickApprove(a);
                }}
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label={`Flag ${a.cinch_account_name ?? a.qb_id}`}
                title="Flag — something looks off (opens notes)"
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded hover:bg-warning-light hover:text-warning",
                  a.review_status === "flagged" ? "text-warning" : "text-muted-foreground",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  openDrawer(a, true);
                }}
              >
                <Flag className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </span>
      ),
    },
  ];

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  if (!isLoading && !pulled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed bg-muted/30 px-4 py-10 text-center">
            {invoicingConfirmed ? (
              <Database className="h-8 w-8 text-muted-foreground" />
            ) : (
              <Lock className="h-8 w-8 text-muted-foreground" />
            )}
            <p className="max-w-md text-sm text-muted-foreground">
              {invoicingConfirmed ? (
                <>
                  {curLabel}'s billed actuals haven't been pulled yet. Pulling
                  snapshots BI.REVENUE_WATERFALL and the raw QuickBooks
                  invoices into this close so the numbers are frozen as
                  reviewed.
                </>
              ) : (
                <>
                  Waiting on{" "}
                  <Link
                    to="/reconciliation/$period/$stepKey"
                    params={{ period, stepKey: "R-00" }}
                    className="font-medium text-brand hover:underline"
                  >
                    R-00
                  </Link>
                  : the controller must sign off that {curLabel}'s invoicing is
                  entered and final before billed actuals can be pulled — this
                  step captures the numbers the whole close treats as final.
                </>
              )}
            </p>
            {invoicingConfirmed && canAct && (
              <Button onClick={doPull} disabled={pull.isPending}>
                <RefreshCw className={cn("mr-1.5 h-4 w-4", pull.isPending && "animate-spin")} />
                {pull.isPending ? "Pulling…" : "Pull from Snowflake"}
              </Button>
            )}
            {invoicingConfirmed && !canAct && (
              <p className="text-xs text-muted-foreground">
                An R-01 owner (RevOps) runs the pull.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Roll-up tiles — computed client-side over ALL accounts */}
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
      >
        <KpiTile
          label={`Billed · ${curLabel}`}
          value={fmtCur2(all.current)}
          sub={
            <>
              <DeltaCell value={all.current - all.prior} className="text-xs" /> vs{" "}
              {priorLabel} ({fmtCur2(all.prior)})
            </>
          }
        />
        <KpiTile
          label="MRR this month"
          value={fmtCur2(all.mrr)}
          sub={
            all.current
              ? `${Math.round((all.mrr / all.current) * 100)}% of billed`
              : undefined
          }
        />
        <KpiTile
          label="QB cross-check"
          value={all.mismatches === 0 ? "In sync" : `${all.mismatches} off`}
          tone={all.mismatches === 0 ? "positive" : "warning"}
          sub={`QB nets ${fmtCur2(all.qb)} · Δ ${fmtCur2(all.qbDelta)}`}
          onClick={() => setSearch({ mismatch: search.mismatch ? undefined : true })}
          selected={!!search.mismatch}
        />
        <KpiTile
          label="Flags"
          value={String(all.flagged)}
          tone={all.flagged > 0 ? "danger" : "positive"}
          sub={
            all.flagged > 0
              ? "must be cleared before R-01 completes"
              : `${all.approved} approved · ${all.pending} pending`
          }
          onClick={() =>
            setSearch({ review: search.review === "flagged" ? undefined : "flagged" })
          }
          selected={search.review === "flagged"}
        />
      </div>

      <FilterBar>
        <FilterSearch
          placeholder="Search account, QB name, or ID…"
          value={filters.q}
          onChange={(v) => setSearch({ q: v || undefined })}
        />
        <FilterChip label="Review" count={filters.review.length}>
          <CheckboxList
            options={REVIEW_LABELS}
            selected={filters.review}
            onChange={(v) => setSearch({ review: v.length ? v.join(",") : undefined })}
          />
        </FilterChip>
        <FilterChip label="Change" count={filters.change.length}>
          <CheckboxList
            options={CHANGE_LABELS}
            selected={filters.change}
            onChange={(v) => setSearch({ change: v.length ? v.join(",") : undefined })}
          />
        </FilterChip>
        <FilterChip
          label="More"
          count={(filters.mismatch ? 1 : 0) + (filters.mrr ? 1 : 0)}
        >
          <CheckboxList
            options={[
              { value: "mismatch", label: "Sources disagree only" },
              { value: "mrr", label: "MRR accounts only" },
            ]}
            selected={[
              ...(filters.mismatch ? ["mismatch"] : []),
              ...(filters.mrr ? ["mrr"] : []),
            ]}
            onChange={(v) =>
              setSearch({
                mismatch: v.includes("mismatch") || undefined,
                mrr: v.includes("mrr") || undefined,
              })
            }
          />
        </FilterChip>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(a) => String(a.qb_id)}
        sort={sort}
        onSortChange={setSort}
        loading={isLoading}
        error={error ? String(error) : null}
        emptyMessage="No accounts match."
        onRowClick={(a) => openDrawer(a, false)}
        selectedKey={search.account != null ? String(search.account) : null}
        containerClassName="max-h-[calc(100vh-14rem)]"
        toolbar={
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {locked && (
              <span className="inline-flex items-center gap-1 font-medium text-success">
                <Lock className="h-3.5 w-3.5" /> Signed off — frozen
              </span>
            )}
            {data?.pulled_at && (
              <span title={new Date(data.pulled_at).toLocaleString()}>
                Pulled {relativeAgo(data.pulled_at)}
                {data.pulled_by_name ? ` by ${data.pulled_by_name}` : ""} ·
                BI.REVENUE_WATERFALL + QB invoices
              </span>
            )}
            {canAct && (
              <Button
                variant="outline"
                size="sm"
                onClick={doPull}
                disabled={pull.isPending || !invoicingConfirmed}
                title={
                  invoicingConfirmed
                    ? undefined
                    : "R-00 was reopened — invoicing must be re-confirmed before re-pulling"
                }
              >
                <RefreshCw
                  className={cn("mr-1.5 h-3.5 w-3.5", pull.isPending && "animate-spin")}
                />
                {pull.isPending ? "Pulling…" : "Re-pull"}
              </Button>
            )}
          </div>
        }
        footer={() => (
          <TableRow>
            <TableCell className="text-xs font-semibold">Total</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {shown.count} account{shown.count === 1 ? "" : "s"}
            </TableCell>
            <TableCell />
            <TableCell numeric>{fmtCur2(shown.prior)}</TableCell>
            <TableCell align="center">
              <DeltaCell value={shown.current - shown.prior} />
            </TableCell>
            <TableCell numeric className="font-semibold">
              {fmtCur2(shown.current)}
            </TableCell>
            <TableCell numeric className="font-semibold">
              {fmtCur2(shown.mrr)}
            </TableCell>
            <TableCell numeric>
              {Math.abs(shown.qbDelta) > 0.01 ? (
                <span className="text-warning">{fmtCur2(shown.qbDelta)}</span>
              ) : (
                <span className="text-muted-foreground">✓</span>
              )}
            </TableCell>
            <TableCell />
          </TableRow>
        )}
      />

      <p className="text-xs text-muted-foreground">
        Total MRR shown: <b className="tabular-nums">{fmtCur2(shown.mrr)}</b>
        {shown.count !== all.count &&
          ` (filtered — ${fmtCur2(all.mrr)} across all ${all.count} accounts)`}
        {" · "}Click an account to see the lines as they appeared on the
        invoice; credit memos are netted in.
      </p>

      <BilledAccountDrawer
        period={period}
        account={drawerAccount}
        canAct={canAct}
        expanded={!!search.expanded}
        presetFlag={flagIntent}
        onExpandedChange={(v) => setSearch({ expanded: v || undefined })}
        onClose={() => {
          setFlagIntent(false);
          setSearch({ account: undefined, expanded: undefined });
        }}
      />
    </div>
  );
}
